import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { themeGet } from 'styled-system';
import { useStore } from '@src/models';
import { ROUTES } from 'src/constants';
import { Flex, BaseLink, TextBox, Box } from 'src/kit';
import { Dropdown, LogoTiny, LogoMedium } from 'src/components';
import { composeFullUrl } from 'src/helpers';
import { Avatar } from '../../molecules/Avatar';
import { CompanyActions } from './components/CompanyActions';
import { AuthActions } from './components/AuthActions';

const links = [
  { label: 'Employers', to: ROUTES.home },
  { label: 'Features', to: ROUTES.features },
  { label: 'Candidates', to: ROUTES.candidates },
  { label: 'Articles', to: ROUTES.articles }
];

const candidateFeaturesRoutes = [
  { label: 'Jobs', to: ROUTES.jobs.base },
  { label: 'Explore', to: ROUTES.magazine },
  { label: 'Resource', to: ROUTES.resources }
];

export const DesktopPublicHeader = observer(function DesktopHeader() {
  const {
    routerStore: {
      location: { pathname: fullPathname }
    },
    authStore: { isAuthorized, logout },
    userStore: { user, defaultProfileRoute }
  } = useStore();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // /jobs/1564 => /jobs
  const truncatedPathname = '/' + fullPathname.split('/')[1];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <Wrapper>
        <DesktopNav alignItems="center" justifyContent="space-between">
          <BaseLink href={ROUTES.home}>
            <DesktopLogo>
              <LogoTiny color="#0d2240" height="34px" />
              <LogoMedium color="primary" mr="26px" height="26px" width="96px" />
            </DesktopLogo>
          </BaseLink>

          {links.map(({ label, to }) => {
            // https://zpl.io/blw5445
            if (
              candidateFeaturesRoutes.find(({ to }) => to === truncatedPathname) &&
              (to === ROUTES.candidates || to === ROUTES.features)
            )
              return false;

            return (
              truncatedPathname !== to && (
                <Flex key={to} height="26px" alignItems="flex-end">
                  <BaseLink href={to} mr={30} fontSize="16px" height="16px" color="textSecondary">
                    {label}
                  </BaseLink>
                </Flex>
              )
            );
          })}

          <DropdownWrapper>
            <Dropdown.Toggle withCaret>
              <Flex height="26px" alignItems="flex-end">
                <TextBox fontSize="16px" height="16px" color="textSecondary">
                  Jobs and resources
                </TextBox>
              </Flex>
            </Dropdown.Toggle>
            <Dropdown.Menu mt={'-10px'} minWidth={150} color="textPrimary">
              {candidateFeaturesRoutes.map(({ label, to }) => (
                <Dropdown.Item key={to}>
                  <BaseLink href={composeFullUrl(to)} mr={30} fontSize="14px">
                    {label}
                  </BaseLink>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </DropdownWrapper>
        </DesktopNav>

        <DesktopUserActions ml="auto" alignItems="center">
          {user?.company && <CompanyActions user={user} />}
          {!isAuthorized ? (
            <AuthActions />
          ) : (
            <Dropdown>
              <Dropdown.Toggle>
                <Avatar
                  src={user?.photo_thumbnail_url}
                  alt="photo"
                  firstName={user?.first_name}
                  size={40}
                  color="header.text"
                  bg="paleGreyTwo"
                  fontSize="22px"
                  fontWeight="normal"
                  lineHeight="38px"
                />
              </Dropdown.Toggle>
              <Dropdown.Menu mt={'-10px'} minWidth={150} color="textSecondary">
                <Dropdown.Item>
                  <BaseLink href={defaultProfileRoute}>My profile</BaseLink>
                </Dropdown.Item>
                <Dropdown.Item>
                  <TextBox onClick={() => logout()}>Logout</TextBox>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </DesktopUserActions>

        <MobileHeaderRow justifyContent="space-between" alignItems="center" width="100%">
          <MobileLogo>
            <BaseLink href={ROUTES.home}>
              <LogoTiny color="#0d2240" height="34px" />
              <LogoMedium color="primary" mr="26px" height="26px" width="96px" />
            </BaseLink>
          </MobileLogo>
          <HamburgerButton $isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} aria-label="Toggle menu">
            <div />
            <div />
            <div />
          </HamburgerButton>
        </MobileHeaderRow>
      </Wrapper>

      <MobileMenu $isOpen={isMobileMenuOpen}>
        <CloseButton onClick={closeMobileMenu} aria-label="Close menu">
          ×
        </CloseButton>
        <MobileNav>
          {links.map(({ label, to }) => {
            if (
              candidateFeaturesRoutes.find(({ to }) => to === truncatedPathname) &&
              (to === ROUTES.candidates || to === ROUTES.features)
            ) {
              return null;
            }
            return (
              truncatedPathname !== to && (
                <Flex key={to} width="100%">
                  <BaseLink
                    href={to}
                    fontSize="18px"
                    color="textSecondary"
                    onClick={closeMobileMenu}
                    display="block"
                    width="100%"
                    py={2}
                  >
                    {label}
                  </BaseLink>
                </Flex>
              )
            );
          })}

          <Flex flexDirection="column" width="100%">
            <TextBox fontSize="18px" color="textSecondary" fontWeight="bold" mb={2}>
              Jobs and resources
            </TextBox>
            {candidateFeaturesRoutes.map(({ label, to }) => (
              <Flex key={to} width="100%" py={1}>
                <BaseLink
                  href={composeFullUrl(to)}
                  fontSize="16px"
                  color="textSecondary"
                  onClick={closeMobileMenu}
                  display="block"
                  width="100%"
                >
                  {label}
                </BaseLink>
              </Flex>
            ))}
          </Flex>

          <Box width="100%" height="1px" bg="iceBlueTwo" my={3} />

          {user?.company && <CompanyActions user={user} />}

          {!isAuthorized ? (
            <AuthActions />
          ) : (
            <Flex flexDirection="column" width="100%" gap={3}>
              <Flex alignItems="center" gap={3}>
                <Avatar
                  src={user?.photo_thumbnail_url}
                  alt="photo"
                  firstName={user?.first_name}
                  size={40}
                  color="header.text"
                  bg="paleGreyTwo"
                  fontSize="22px"
                  fontWeight="normal"
                  lineHeight="38px"
                />
                <TextBox fontSize="16px" color="textPrimary">
                  {user?.first_name} {user?.last_name}
                </TextBox>
              </Flex>
              <Flex flexDirection="column" width="100%">
                <BaseLink
                  href={defaultProfileRoute}
                  fontSize="16px"
                  color="textSecondary"
                  onClick={closeMobileMenu}
                  py={2}
                >
                  My profile
                </BaseLink>
                <TextBox
                  fontSize="16px"
                  color="textSecondary"
                  onClick={() => {
                    logout();
                    closeMobileMenu();
                  }}
                  style={{ cursor: 'pointer' }}
                  py={2}
                >
                  Logout
                </TextBox>
              </Flex>
            </Flex>
          )}
        </MobileNav>
      </MobileMenu>
    </>
  );
});

export const Wrapper = styled(Flex)`
  align-items: center;
  height: 80px;
  padding: 0 60px;
  border-bottom: 1px solid ${themeGet('colors.iceBlueTwo')};
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;

  @media (max-width: 768px) {
    padding: 0 20px;
    height: 60px;
  }

  .dropdown-caret {
    color: ${themeGet('colors.textSecondary')};
    align-self: flex-end;
  }
`;

const DropdownWrapper = styled(Dropdown)`
  li {
    padding: 0;
    a {
      padding: 0.5rem 1rem;
      width: 100%;
      margin-right: 0;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileLogo = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
  }
`;

const DesktopLogo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopNav = styled(Flex)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopUserActions = styled(Flex)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;

  @media (max-width: 768px) {
    display: flex;
  }

  &:focus {
    outline: none;
  }

  div {
    width: 30px;
    height: 3px;
    background: ${themeGet('colors.textSecondary')};
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;

    &:first-child {
      transform: ${props => (props.$isOpen ? 'rotate(45deg) translate(6px, 6px)' : 'none')};
    }

    &:nth-child(2) {
      opacity: ${props => (props.$isOpen ? '0' : '1')};
    }

    &:last-child {
      transform: ${props => (props.$isOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none')};
    }
  }
`;

// Mobile menu overlay
const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  z-index: 999;
  padding: 80px 20px 20px;
  transform: ${props => (props.$isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 0.3s ease;
  overflow-y: auto;

  @media (min-width: 769px) {
    display: none;
  }
`;

// Navigation container for mobile
const MobileNav = styled(Flex)`
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

// Close button for mobile menu
const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${themeGet('colors.textSecondary')};

  &:hover {
    color: ${themeGet('colors.textPrimary')};
  }
`;

const MobileHeaderRow = styled(Flex)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
  }
`;
